import * as React from 'react'
import styled from 'styled-components'
import { Colors, Inset, Text } from '@doordash/design-language'

interface StepIndicatorProps {
  totalStep: number
  currentStep?: number
}

const Wrapper = styled(Inset)`
  display: flex;
  align-items: center;
`

interface StepProps {
  isCompleted?: boolean
}

const Step = styled.div`
  height: 25px;
  width: 25px;
  border-radius: 50%;
  background: ${(props: StepProps) =>
    props.isCompleted
      ? Colors.BackgroundPrimaryInverted
      : Colors.BackgroundTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
`

const LineWrapper = styled.div`
  position: relative;
  flex: 1;
`

const Line = styled.div`
  height: 3px;
  background: ${(props: StepProps) =>
    props.isCompleted
      ? Colors.BackgroundPrimaryInverted
      : Colors.BackgroundTertiary};
`

const SecondaryLine = styled(Line)`
  background: ${Colors.BackgroundPrimaryInverted};
  width: 50%;
  position: absolute;
  top: 0;
  left: 0;
`

interface CourseStep {
  step: number
  status: StepStatus
}

type StepStatus = 'completed' | 'current' | 'upcoming'

export function getSteps(totalStep: number, currentStep: number): CourseStep[] {
  const steps: CourseStep[] = Array.from({ length: totalStep }, (_, i) => {
    const step = i + 1

    let status: StepStatus = 'upcoming'
    if (step < currentStep) {
      status = 'completed'
    }

    if (step === currentStep) {
      status = 'current'
    }

    return {
      step,
      status,
    }
  })

  return steps
}

export function StepIndicator(props: StepIndicatorProps) {
  const { totalStep, currentStep = 1 } = props
  const steps = getSteps(totalStep, currentStep)

  return (
    <Wrapper>
      {steps.map(item => {
        const { step, status } = item
        const isCompleted = status === 'completed'
        const isCurrent = status === 'current'

        const line =
          step < totalStep ? (
            <LineWrapper>
              <Line isCompleted={isCompleted} />
              {isCurrent ? <SecondaryLine /> : null}
            </LineWrapper>
          ) : null
        const isActive = isCompleted || isCurrent

        return (
          <React.Fragment key={step}>
            <Step isCompleted={isActive}>
              <Text
                color={
                  isActive ? Colors.TextPrimaryOnDark : Colors.TextSecondary
                }
                size={Text.Sizes[300]}
              >
                {step}
              </Text>
            </Step>
            {line}
          </React.Fragment>
        )
      })}
    </Wrapper>
  )
}
